import { v4 as uuidv4 } from 'uuid';

/**
 * Generates a unique device signature based on browser/device characteristics
 * @returns {Promise<string>} A promise that resolves to a unique device signature
 */
export const generateDeviceSignature = async () => {
  try {
    // Get browser and device information
    const userAgent = window.navigator.userAgent;
    const language = window.navigator.language;
    const colorDepth = window.screen.colorDepth;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const hardwareConcurrency = window.navigator.hardwareConcurrency || '';
    const deviceMemory = window.navigator.deviceMemory || '';
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    const availableScreenResolution = `${window.screen.availWidth}x${window.screen.availHeight}`;
    const pixelRatio = window.devicePixelRatio || '';
    
    // Try to get WebGL fingerprint
    let webglVendor = '';
    let webglRenderer = '';
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl) {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          webglVendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '';
          webglRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
        }
      }
    } catch (e) {
      console.warn('Could not get WebGL info:', e);
    }

    // Get timezone offset
    const timezoneOffset = new Date().getTimezoneOffset();
    
    // Get plugins
    const plugins = [];
    if (navigator.plugins) {
      for (let i = 0; i < navigator.plugins.length; i++) {
        plugins.push(navigator.plugins[i].name);
      }
    }

    // Create a fingerprint object
    const fingerprint = {
      userAgent,
      language,
      colorDepth,
      timezone,
      timezoneOffset,
      hardwareConcurrency,
      deviceMemory,
      screenResolution,
      availableScreenResolution,
      pixelRatio,
      webglVendor,
      webglRenderer,
      plugins: plugins.join(','),
      // Add more attributes as needed
    };

    // Generate a stable ID from the fingerprint
    const fingerprintString = JSON.stringify(fingerprint);
    
    // Use a combination of hashing and localStorage to maintain a consistent ID
    const storedId = localStorage.getItem('deviceFingerprint');
    if (storedId && typeof storedId === 'string') {
      return storedId;
    }
    
    // If no stored ID, generate a new one
    const newId = String(await hashString(fingerprintString));
    localStorage.setItem('deviceFingerprint', newId);
    return newId;
    
  } catch (error) {
    console.error('Error generating device fingerprint:', error);
    // Fallback to a random UUID if fingerprinting fails
    return uuidv4();
  }
};

/**
 * Hashes a string using the Web Crypto API
 * @param {string} str - The string to hash
 * @returns {Promise<string>} A promise that resolves to the hash
 */
async function hashString(str) {
  try {
    // Encode the string as a Uint8Array
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    
    // Generate the hash
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    
    // Convert the hash to a hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
      
    return hashHex;
  } catch (error) {
    console.error('Error hashing string:', error);
    // Fallback to a simple hash if Web Crypto is not available
    return Array.from(str)
      .reduce((hash, char) => {
        const updatedHash = ((hash << 5) - hash) + char.charCodeAt(0);
        return updatedHash & updatedHash;
      }, 0)
      .toString();
  }
}

// Export a singleton instance
export const deviceFingerprint = {
  get: generateDeviceSignature
};
